import api from './axiosInstance';

export interface MenuItem {
  id: string;
  name: string;
  path: string;
  icon: string;
  permissionKey: string;
  parentKey?: string;
  order: number;
  subItems?: MenuItem[];
}

/**
 * Fetch menu items based on user permissions
 * This function calls the backend API to get filtered menu items
 * @param department - Optional active department (OMS, AMS, EPC) for department-specific menu filtering
 */
export const fetchMenuItems = async (department?: string): Promise<MenuItem[]> => {
  try {
    const params = department ? { department } : {};
    const response = await api.get('/features/menu-items', { params });
    
    // Ensure we always return an array
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // Handle paginated response if accidentally returned
    if (response.data && Array.isArray(response.data.docs)) {
      return response.data.docs;
    }
    
    // Handle any other response format
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    console.warn('Unexpected response format from menu items API:', response.data);
    return [];
  } catch (error) {
    console.error('Error fetching menu items:', error);
    return [];
  }
};

/**
 * Get all features (admin only)
 *
 * Uses the non-paginated `/features/all-for-business-unit` endpoint so the
 * Menu Configuration picker sees every feature, not just the first page.
 */
export const fetchAllFeatures = async (businessUnit: string = 'ALL'): Promise<{ docs?: MenuItem[]; data?: MenuItem[] } | MenuItem[]> => {
  try {
    const response = await api.get('/features/all-for-business-unit', {
      params: { businessUnit },
    });

    if (response.data && Array.isArray(response.data.data)) {
      return response.data;
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    console.warn('Unexpected response format from features API:', response.data);
    return [];
  } catch (error) {
    console.error('Error fetching all features:', error);
    return [];
  }
};

/**
 * Create a new feature (admin only)
 */
export const createFeature = async (featureData: Partial<MenuItem>): Promise<MenuItem> => {
  const response = await api.post('/features', featureData);
  return response.data;
};

/**
 * Update a feature (admin only)
 */
export const updateFeature = async (key: string, featureData: Partial<MenuItem>): Promise<MenuItem> => {
  const response = await api.put(`/features/${key}`, featureData);
  return response.data;
};

/**
 * Delete a feature (admin only)
 */
export const deleteFeature = async (key: string): Promise<void> => {
  await api.delete(`/features/${key}`);
};

/**
 * Initialize default features (admin only)
 */
export const initializeFeatures = async (): Promise<void> => {
  await api.post('/features/initialize');
}; 